/**
 * useProductModal.js
 * ─────────────────────────────────────────────────────────
 * Custom hook that manages the lifecycle of the Product Details Modal.
 *
 * Responsibilities:
 *  - Track open/closed state
 *  - Hold the product currently being displayed
 *  - Lock / unlock body scroll when the modal is open
 *  - Expose openModal(product) and closeModal() actions
 *
 * This hook is intentionally kept lean so the same modal can
 * be repurposed as an "Edit Product" modal in the future – just
 * call openModal(product, { mode: 'edit' }) and forward the
 * extra context down to the modal component.
 */
import { useState, useEffect, useCallback } from 'react';

/**
 * @returns {{
 *   isOpen: boolean,
 *   product: object|null,
 *   openModal: (product: object) => void,
 *   closeModal: () => void
 * }}
 */
export function useProductModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [product, setProduct] = useState(null);

  /**
   * Lock body scroll whenever the modal is open.
   * Restores on cleanup / close.
   */
  useEffect(() => {
    if (isOpen) {
      // Save the current scroll position and lock
      // eslint-disable-next-line prefer-destructuring
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflowY = 'scroll';
      document.body.classList.add('modal-open');
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflowY = '';
      document.body.classList.remove('modal-open');

      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    }
    return () => {
      // Cleanup on unmount
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflowY = '';
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  /** Open the modal with the given product data object */
  const openModal = useCallback((productData) => {
    setProduct(productData);
    setIsOpen(true);
  }, []);

  /** Close the modal and clear the product state */
  const closeModal = useCallback(() => {
    setIsOpen(false);
    // Delay clearing product so exit animation can finish
    setTimeout(() => setProduct(null), 350);
  }, []);

  return { isOpen, product, openModal, closeModal };
}
